//
// [MODIFIED FILE]: apps/web/src/uploads/pnl-confirmation-modal.ts
//
import { showLoading, hideLoading } from '@/core/ui'
import { formatCurrency } from '@/utils/string'
import { saveConfirmedPnl } from '@/uploads/pnl'
import { getAccountContext } from '@/store' 

/**
 * Renders the list of suggested items into the confirmation modal.
 */
function renderPnlConfirmationRows(
  container: HTMLElement,
  items: any[],
  categories: string[]
) {
  const categoryOptions = categories.map(c => `<option value="${c}">${c}</option>`).join('')
  
  container.innerHTML = items.map((item, index) => {
    const suggestion = item.suggestedCategory
    const options = categories.map(c => 
      `<option value="${c}" ${c === suggestion ? 'selected' : ''}>${c}</option>`
    ).join('')

    const isNewRow = item.isNew === true;

    return `
      <tr class="border-b" data-row-index="${index}">
        <td class="p-2">
          ${isNewRow
            ? `<input type="text" data-field="item" class="pnl-item-input w-full p-1 border rounded-md" value="${item.item}" placeholder="Enter item name">`
            : `<span class="text-sm text-gray-800">${item.item}</span>`
          }
        </td>
        <td class="p-2">
          ${isNewRow
            ? `<input type="number" data-field="value" class="pnl-value-input w-full p-1 border rounded-md" value="${item.value}">`
            : `<span class="text-sm text-gray-900 font-mono">${formatCurrency(item.value)}</span>`
          }
        </td>
        <td class="p-2">
          <select data-field="category" class="pnl-category-select w-full p-1 border rounded-md">
            ${options}
          </select>
        </td>
        <td class="p-2 text-center">
          ${isNewRow
            ? `<button class="pnl-save-row text-blue-600 hover:text-blue-800" title="Save row">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </button>`
            : `<button class="pnl-delete-row text-gray-400 hover:text-red-600" title="Delete row">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
              </button>`
          }
        </td>
      </tr>
    `
  }).join('')
}

/**
 * Displays the AI P&L Confirmation Modal
 */
export function showPnlConfirmationModal(data: {
  fileName: string,
  branchId: string,
  branchName: string,
  period: string,
  suggestions: any[],
  onSaveComplete: () => Promise<void> // Generic callback
}) {
  // Create and show a modal
  const modalId = 'ai-pnl-confirmation-modal'
  let modal = document.getElementById(modalId)
  if (modal) modal.remove() // Remove old one if exists

  const validCategories = [
    'Pendapatan (Revenue)', 'Harga Pokok Produksi', 'Beban Operasional (OPEX)',
    'Beban Non Operasional', 'Depresiasi/ Amortisasi', 'Bunga', 'Pajak (PB1)',
  ]
  
  modal = document.createElement('div')
  modal.id = modalId
  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'
  modal.style.backdropFilter = 'blur(4px)'
  
  modal.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[90vh] flex flex-col">
      <div class="p-4 border-b flex justify-between items-center">
        <h2 class="text-xl font-bold text-gray-900">Confirm P&L Data</h2>
        <button id="pnl-confirm-close" class="text-gray-400 hover:text-gray-600">&times;</button>
      </div>
      
      <div class="p-4 bg-gray-50 grid grid-cols-3 gap-4">
        <div>
          <label class="text-xs font-medium text-gray-500">File Name</label>
          <p class="text-sm font-medium text-gray-900">${data.fileName}</p>
        </div>
        <div>
          <label for="pnl-confirm-branch" class="text-xs font-medium text-gray-500">Assign to Branch</label>
          <select id="pnl-confirm-branch" class="w-full p-1 border rounded-md mt-1 text-sm">
            </select>
        </div>
        <div>
          <label for="pnl-confirm-period" class="text-xs font-medium text-gray-500">Assign to Period (YYYY-MM)</label>
          <input type="text" id="pnl-confirm-period" class="w-full p-1 border rounded-md mt-1 text-sm" value="${data.period || ''}" placeholder="YYYY-MM">
        </div>
      </div>
      
      <div class="flex-1 overflow-y-auto p-4">
        <table class="min-w-full">
          <thead class="bg-gray-100 sticky top-0">
            <tr>
              <th class="p-2 text-left text-sm font-semibold text-gray-700 w-2/5">Item</th>
              <th class="p-2 text-left text-sm font-semibold text-gray-700 w-1/5">Value</th>
              <th class="p-2 text-left text-sm font-semibold text-gray-700 w-2/5">Assign to Category</th>
              <th class="p-2 text-center text-sm font-semibold text-gray-700"></th>
            </tr>
          </thead>
          <tbody id="pnl-confirm-rows">
            </tbody>
        </table>
      </div>
      
      <div class="p-4 border-t bg-gray-50 flex justify-between items-center gap-3">
        <button id="pnl-add-row" class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">+ Add Item</button>
        
        <div class="flex-1 text-right">
          <span id="pnl-confirm-error" class="text-red-600 text-sm mr-4"></span>
          <button id="pnl-confirm-cancel" class="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
          <button id="pnl-confirm-save" class="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 ml-2">Confirm & Save</button>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(modal)
  
  const rowsContainer = document.getElementById('pnl-confirm-rows') as HTMLElement
  const errorEl = document.getElementById('pnl-confirm-error') as HTMLElement
  const saveBtn = document.getElementById('pnl-confirm-save') as HTMLButtonElement
  const cancelBtn = document.getElementById('pnl-confirm-cancel') as HTMLButtonElement
  const closeBtn = document.getElementById('pnl-confirm-close') as HTMLButtonElement
  const addRowBtn = document.getElementById('pnl-add-row') as HTMLButtonElement
  const branchSelectEl = document.getElementById('pnl-confirm-branch') as HTMLSelectElement;
  const periodInputEl = document.getElementById('pnl-confirm-period') as HTMLInputElement;

  // --- [POPULATE BRANCH DROPDOWN] ---
  const { userBranches, selectedBrandId } = getAccountContext(); 

  // 1. Filter by Brand
  let branchesForBrand = selectedBrandId
      ? userBranches.filter(branch => branch.brandId === selectedBrandId)
      : userBranches;

  // --- [THIS IS THE FIX] ---
  // 2. Deduplicate branches by ID
  // This ensures that even if 'userBranches' has duplicates, the dropdown is clean.
  const uniqueMap = new Map();
  branchesForBrand.forEach(b => uniqueMap.set(b.branchId, b));
  branchesForBrand = Array.from(uniqueMap.values());
  // --- [END FIX] ---

  if (!branchesForBrand || branchesForBrand.length === 0) {
      branchSelectEl.innerHTML = '<option value="">No branches found for this brand</option>';
      branchSelectEl.disabled = true;
  } else {
      branchSelectEl.innerHTML = branchesForBrand
          .map(branch => `<option value="${branch.branchId}">${branch.branchName}</option>`)
          .join('');
      
      // Set default selection
      const aiGuessedBranch = branchesForBrand.find(b => b.branchId === data.branchId || b.branchName === data.branchName);
      if (aiGuessedBranch) {
          branchSelectEl.value = aiGuessedBranch.branchId;
      } else {
          branchSelectEl.value = branchesForBrand[0].branchId; 
      }
  }

  const closeModal = () => modal?.remove()
  cancelBtn.addEventListener('click', closeModal)
  closeBtn.addEventListener('click', closeModal)
  
  let currentSuggestions = [...data.suggestions]
  
  const reRenderTable = () => {
    renderPnlConfirmationRows(rowsContainer, currentSuggestions, validCategories)
  }
  
  reRenderTable() // Initial render

  // Event listener for Add, Delete, and Save
  rowsContainer.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    const row = target.closest('tr')
    if (!row) return

    const index = parseInt(row.dataset.rowIndex || '0')
    const item = currentSuggestions[index]

    // Handle Delete
    if (target.closest('.pnl-delete-row')) {
      currentSuggestions.splice(index, 1) // Remove item from state
      reRenderTable() // Re-render the table
    }

    // Handle Save
    if (target.closest('.pnl-save-row')) {
      if (!item.item || item.item.trim() === '') {
        alert('Please enter an item name before saving.')
        return
      }
      item.isNew = false // Flip the flag
      reRenderTable() // Re-render the table
    }
  })

  // Listener for live editing of inputs AND dropdowns
  rowsContainer.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement
    const row = target.closest('tr')
    if (!row) return
    
    const index = parseInt(row.dataset.rowIndex || '0')
    const field = target.dataset.field as 'item' | 'value' | 'category'
    
    if (!field || !currentSuggestions[index]) return
    
    if (field === 'value') {
      currentSuggestions[index].value = parseFloat(target.value) || 0
    } else if (field === 'item') {
      currentSuggestions[index].item = target.value
    } else if (field === 'category') {
      currentSuggestions[index].suggestedCategory = target.value
    }
  })
  
  // Handle Add Row
  addRowBtn.addEventListener('click', () => {
    currentSuggestions.push({
      item: '', // Start with an empty item
      value: 0,
      suggestedCategory: 'Beban Non Operasional',
      isNew: true // Set the 'isNew' flag
    })
    reRenderTable()
    rowsContainer.parentElement?.scrollTo(0, rowsContainer.parentElement.scrollHeight)
  })

  // Handle the final (main) save
  saveBtn.addEventListener('click', async () => {
    // Read values from the inputs
    const selectedBranchId = branchSelectEl.value;
    const selectedPeriod = periodInputEl.value;
    
    const selectedBranch = branchesForBrand.find(b => b.branchId === selectedBranchId);

    // Add validation
    if (!selectedBranch) {
        errorEl.textContent = 'Error: Please select a valid branch.';
        return; 
    }
    if (!/^\d{4}-\d{2}$/.test(selectedPeriod)) {
        errorEl.textContent = 'Error: Period must be in YYYY-MM format.';
        periodInputEl.classList.add('border-red-500'); // Highlight the bad input
        return; 
    } else {
        periodInputEl.classList.remove('border-red-500');
    }

    const uninitializedRow = currentSuggestions.find(s => !s.item || s.item.trim() === '')
    if (uninitializedRow) {
      errorEl.textContent = 'Error: Please provide a name for all items before saving.'
      const rowEl = rowsContainer.querySelector(`[data-row-index="${currentSuggestions.indexOf(uninitializedRow)}"]`)
      rowEl?.querySelector('input[data-field="item"]')?.classList.add('border-red-500')
      return
    }
    
    const unsavedRow = currentSuggestions.find(s => s.isNew === true)
    if (unsavedRow) {
       errorEl.textContent = 'Error: Please click the blue checkmark to save all new rows before confirming.'
       return
    }
    
    saveBtn.disabled = true
    saveBtn.textContent = 'Saving...'
    showLoading({ message: 'Saving confirmed P&L data...' })
    
    try {
      const payload = {
        branchId: selectedBranchId,
        branchName: selectedBranch.branchName,
        period: selectedPeriod,
        fileName: data.fileName,
        approvedItems: currentSuggestions.map(s => ({
          item: s.item,
          value: s.value,
          category: s.suggestedCategory, // This now contains the user's change
        }))
      }
      
      await saveConfirmedPnl(payload)
      
      hideLoading()
      alert('P&L data saved successfully!')
      closeModal()
      
      await data.onSaveComplete()

    } catch (error: any) {
      console.error('Error saving P&L data:', error)
      errorEl.textContent = error.message
      saveBtn.disabled = false
      saveBtn.textContent = 'Confirm & Save'
      hideLoading()
    }
  })
}